import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";
import CampaignMappingModal from "./CampaignMappingModal";
import { createProductSetModalStyle as style } from "../productSetsStyles";

const frequencies = ["1 Day", "3 Days", "7 Days", "14 Days", "21 Days"];

export default function CreateProductSetModal({
  open,
  onClose,
  onSave,
  isSaving,
  productSetName,
  productSetRefreshFrequency,
  productSetDeDuplicateASIN,
  errors,
  handleProductSetDetail,
  productSetCreationId,
  userPlatform,
  productsetId,
}) {
  const navigate = useNavigate();
  const [textValue, setTextValue] = useState(productSetName || "");
  const [mapCampaign, setMapCampaign] = useState(false);

  // debounce function to handle lagging issue with input
  // This will delay the update to the parent form by 500ms
  const updateParentForm = useMemo(
    () =>
      debounce((value) => {
        handleProductSetDetail("name", value);
      }, 500),
    []
  );

  useEffect(() => {
    setTextValue(productSetName || "");
    // Cleanup the debounce function on unmount
    return () => {
      updateParentForm.cancel();
    };
  }, [productSetName]);

  if (mapCampaign) {
    return (
      <CampaignMappingModal
        open={mapCampaign}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setMapCampaign(false);
        }}
        productsetId={productSetCreationId}
        userPlatform={userPlatform}
      />
    );
  }

  if (open === "CREATED" || open === "UPDATED") {
    return (
      <Modal
        open={!!open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
      >
        <Box sx={style} textAlign={"center"}>
          <Box display="flex" flexDirection="column" alignItems="center" p={4}>
            <Box
              mb={2}
              display={"flex"}
              justifyContent="center"
              alignItems="center"
              height={76}
              width={76}
              borderRadius={"100%"}
              backgroundColor={"#EDF7ED"}
            >
              <DoneIcon
                sx={{
                  color: "#2E7D32",
                  fontSize: "48px",
                }}
              />
            </Box>
            <Typography variant="subtitle1">
              {open === "CREATED"
                ? "Product Set Created Successfully!"
                : "Product Set Updated Successfully!"}
            </Typography>
            <Typography variant="body2" color="text.secondary" m={0} mb={2}>
              {open === "CREATED"
                ? "Your product set has been created successfully."
                : "Your product set has been updated successfully."}
            </Typography>
            {open === "CREATED" && (
              <Typography variant="subtitle1">
                Would you like to link this product set to a campaign?
              </Typography>
            )}
          </Box>

          <Divider sx={{ background: "#E0E0E0" }} />

          {/* Actions */}
          {open === "CREATED" ? (
            <Box display="flex" gap={1} p={2.5}>
              <Button
                onClick={() => {
                  navigate("/product-set-list");
                }}
                variant="outlined"
                size="medium"
                fullWidth
                sx={{ flex: 1 }}
              >
                Go to Product Set List
              </Button>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  backgroundColor: "#6759FF",
                  flex: 1,
                  position: "relative",
                }}
                onClick={() => {
                  setMapCampaign(true);
                }}
              >
                Link to Campaign
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={1} p={2.5}>
              <Button
                onClick={onClose}
                variant="outlined"
                size="medium"
                fullWidth
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  backgroundColor: "#6759FF",
                  flex: 1,
                  position: "relative",
                }}
                onClick={() => {
                  navigate("/product-set-list");
                }}
              >
                Go to Product Set List
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={!!open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
    >
      <Box sx={style}>
        {/* Modal Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          px={2.5}
          pt={2.5}
        >
          <Typography variant="subtitle1" color="text.primary">
            Create Product Set
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ background: "#E0E0E0" }} />

        {/* Subheading */}
        <Typography variant="subtitle1" color="text.primary" mt={2.5} px={2.5}>
          Product Set Details
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" px={2.5}>
          Product Listing file is Base feed with product details
        </Typography>

        {/* Form Fields */}
        <Box display="flex" flexDirection="column" gap={1} p={2.5} mb={4}>
          <TextField
            label="Product Set Name"
            sx={{ mb: 2 }}
            required
            placeholder="Enter Product Set Name"
            fullWidth
            size="medium"
            value={textValue}
            error={!!errors?.name}
            helperText={errors?.name}
            onChange={(e) => {
              setTextValue(e.target.value);
              updateParentForm(e.target.value);
            }}
          />

          <TextField
            label="Select Frequency"
            required
            select
            fullWidth
            size="medium"
            value={productSetRefreshFrequency}
            onChange={(e) =>
              handleProductSetDetail("refresh_duration", e.target.value)
            }
          >
            {frequencies.map((freq) => (
              <MenuItem key={freq} value={freq}>
                {freq}
              </MenuItem>
            ))}
          </TextField>

          {userPlatform === "amazon" && (
            <FormControlLabel
              sx={{
                cursor: "pointer",
                "& .MuiFormControlLabel-label": {
                  cursor: "pointer",
                },
              }}
              control={
                <Checkbox
                  checked={productSetDeDuplicateASIN}
                  onChange={() =>
                    handleProductSetDetail(
                      "de_duplication",
                      !productSetDeDuplicateASIN
                    )
                  }
                />
              }
              label="De Duplication Of ASIN's"
            />
          )}
        </Box>

        <Divider sx={{ background: "#E0E0E0" }} />

        {/* Actions */}
        <Box display="flex" gap={1} p={2.5}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="medium"
            fullWidth
            sx={{ flex: 1 }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="medium"
            sx={{ backgroundColor: "#6759FF", flex: 1, position: "relative" }}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress
                size={24}
                color="inherit"
                sx={{
                  color: "white",
                }}
              />
            ) : productsetId ? (
              "Update Product Set"
            ) : (
              "Create Product Set"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
